import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import LandingMarketing from "./LandingMarketing";
import { RouteFallback } from "@/components/RouteFallback";

const LandingAuthenticatedGate = lazy(() => import("./LandingAuthenticatedGate"));

/**
 * Anonymous marketing shell has no static Supabase import — session check uses dynamic import so auth/realtime
 * stay out of the initial JS for typical visitors.
 */
export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  /** `undefined` = session probe not finished yet (still show marketing for LCP). */
  const [sessionUser, setSessionUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    const search = window.location.search || "";
    const hash = window.location.hash || "";

    // Email confirmation and OAuth PKCE links may land on `/` when Supabase falls back to Site URL.
    // Route them through the standard auth callback — not admin impersonation.
    if (search.includes("code=")) {
      navigate({ pathname: "/auth/callback", search }, { replace: true });
      return;
    }
    if (hash.includes("access_token=") && hash.includes("refresh_token=")) {
      navigate(
        { pathname: "/admin/impersonate-callback", hash: hash.replace(/^#/, "") },
        { replace: true },
      );
    }
  }, [navigate]);

  useEffect(() => {
    void import("@/integrations/supabase/client").then(async ({ supabase }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setSessionUser(user ?? null);
    });
  }, []);

  useEffect(() => {
    if (sessionUser) return;
    const hash = location.hash?.replace(/^#/, "");
    if (!hash) return;
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.hash, sessionUser]);

  if (sessionUser) {
    return (
      <Suspense fallback={<RouteFallback label="Loading your workspace..." />}>
        <LandingAuthenticatedGate user={sessionUser} />
      </Suspense>
    );
  }

  return <LandingMarketing />;
}
